import _toConsumableArray from "/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { darken, getLuminance, lighten, parseToHsl, parseToRgb, readableColor } from 'polished';
import { config } from '.';
import { BREAKPOINTS, MAX_BREAKPOINTS } from '../themes/breakpoints';
import { skinVariables } from '../themes/globalVariables';
var isLocal = config.isLocal;

// minimize in prod
var space = isLocal ? ' ' : '';
var lineBreak = isLocal ? '\n' : '';

/**
 * @type {Object<string, string>}
 * @description Object with color variables. Used on Override Background.
 * @example
 * {
 *   '--g-color-primary': '210, 100%, 50%',
 *   '--g-color-primary-50': '210, 100%, 95%',
 *   '--g-color-primary-100': '210, 100%, 90%',
 *   ...
 * }
 */
var dynamicStylesColors = {
  '--g-color-surface': skinVariables.color['--g-color-surface'],
  '--g-color-surface-dim': skinVariables.color['--g-color-surface-dim'],
  '--g-color-surface-bright': skinVariables.color['--g-color-surface-bright'],
  '--g-color-inverse-surface': skinVariables.color['--g-color-inverse-surface']
};

/**
 * @type {Set<string>}
 * @description Set of strings
 * @example
 * { 'greycliff-cf', 'Roboto, Lato' }
 */
var dynamicStylesFonts = new Set();

/**
 * Local Dynamic Fonts names are stored in dynamicStylesFonts and passed to GlobalStyles. 
 * There, font-faces are generated if the font exist in dynamicFonts object (dynamicFonts.js).
 * @param {string} stylesImport
 * @param {string} font
 * @returns {boolean|void}
 */
function storeFontImports(stylesImport, font) {
  if (stylesImport.replaceAll(' ', '') === '{{@import}}') {
    dynamicStylesFonts.add(font);
    return true;
  }
  return false;
}
function parseCSS(json) {
  var depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var removeImportant = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var tab = isLocal ? '  '.repeat(depth) : '';
  var css = '';
  if (depth > 99) return;
  if (typeof json === 'object') {
    var _loop = function _loop(_selector) {
      if (typeof json[_selector] === 'object') {
        if (_selector.startsWith('@keyframes')) {
          removeImportant = true;
        }
        var block = parseCSS(json[_selector], depth + 1, removeImportant);
        if (_selector.startsWith('@media')) {
          Object.keys(BREAKPOINTS).forEach(function (breakpoint) {
            _selector = _selector.replace('-width: ', '-width:').replace("{{ ".concat(breakpoint, " }}"), "{{".concat(breakpoint, "}}"));
            _selector = _selector.replace("min-width:{{".concat(breakpoint, "}}"), "min-width:".concat(BREAKPOINTS[breakpoint]));
            _selector = _selector.replace("max-width:{{".concat(breakpoint, "}}"), "max-width:".concat(MAX_BREAKPOINTS[breakpoint]));
          });
        }
        css += "".concat(tab).concat(_selector).concat(space, "{").concat(lineBreak);
        css += block;
        css += "}".concat(lineBreak.repeat(2));
      } else {
        /*We don't add local dynamic font imports to the css, they are added in GlobalStyles.js.
        Fonts in public folder are not cached and since the fonts have the hash value,
        we can't import it in the dynamic CSS string. Font-faces are generated in FontList.js */
        var hasDynamicFontImports = storeFontImports(_selector, json[_selector]);
        if (hasDynamicFontImports) {
          selector = _selector;
          return "continue";
        }

        // TODO: remove all importants after applying cascade layers
        var value = json[_selector].toString().replace('!important', '');
        var property = _selector;
        if (_selector.startsWith('@import')) {
          css += "".concat(tab).concat(_selector, ";").concat(lineBreak);
          selector = _selector;
          return "continue";
        }
        if (property.startsWith('--g-color-')) {
          css += generateColorVariables(property, value, tab);
        } else {
          var important = removeImportant ? '' : '!important';
          if (value.match('!noimportant')) {
            important = '';
            value = value.replace('!noimportant', '');
          }
          css += "".concat(tab).concat(property, ":").concat(space).concat(value).concat(important, ";").concat(lineBreak);
        }
      }
      selector = _selector;
    };
    for (var selector in json) {
      var _ret = _loop(selector);
      if (_ret === "continue") continue;
    }
  }
  return css;
}
export function jsonToCss(stylesArray) {
  if (!stylesArray) return;
  var css = '';
  _toConsumableArray(stylesArray).forEach(function (styles) {
    css += parseCSS(styles.styles);
  });
  return css;
}

/**
 * Used Predefined Themes. Stores a CSS property to be used as background, and color contrast when a variable
 * is selected in Override Background entries, used in component styles when getOverrideBackground and
 * isBgColorStyleLight are called.
 * 
 * @param {string} property Color variable
 * @param {string} color Color value
 * @returns {string} HSL color value
 */
export function storeAndConvertToHsl(property, color) {
  var hsl = convertToHsl(color);
  storeColorVariable(property, hsl);
  return hsl;
}
export function convertToHsl(color) {
  if (!color) return;
  if (color.includes('--')) {
    return color;
  }
  var _parseToHsl = parseToHsl(color),
    hue = _parseToHsl.hue,
    saturation = _parseToHsl.saturation,
    lightness = _parseToHsl.lightness;
  var h = Math.round(hue);
  var s = Math.round(saturation * 100);
  var l = Math.round(lightness * 100);
  return "".concat(h, " ").concat(s, "% ").concat(l, "%");
}
export function convertToRgba(color) {
  if (!color) return;
  if (color.includes('--')) {
    return color;
  }
  var _parseToRgb = parseToRgb(color),
    red = _parseToRgb.red,
    green = _parseToRgb.green,
    blue = _parseToRgb.blue,
    alpha = _parseToRgb.alpha;
  var r = Math.round(red);
  var g = Math.round(green);
  var b = Math.round(blue);
  if (!alpha) {
    return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", 1)");
  }
  var a = alpha === null || alpha === void 0 ? void 0 : alpha.toFixed(2);
  return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(a, ")");
}
function findMinDarkenValue(luminance, color) {
  var limit = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  if (limit > 99) return 0.1;
  var tryLuminanceValue = luminance;
  var tryColor = darken(tryLuminanceValue, color);
  var tryLuminance = getLuminance(tryColor);
  if (tryLuminance < 0.01) {
    return tryLuminanceValue;
  }
  return findMinDarkenValue(luminance + 0.02, color, limit + 1);
}
function findMaxLightenValue(luminance, color) {
  var limit = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  if (limit > 99) return 0.1;
  var tryLuminanceValue = 1 - luminance;
  var tryColor = lighten(tryLuminanceValue, color);
  var tryLuminance = getLuminance(tryColor);
  if (tryLuminance < 0.98) {
    return tryLuminanceValue;
  }
  return findMaxLightenValue(luminance + 0.02, color, limit + 1);
}
function generateColorShades(color) {
  var shades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
  var luminance = getLuminance(color);
  var maxLightenValue = findMaxLightenValue(luminance, color);
  var maxDarkenValue = findMinDarkenValue(0.00052, color);
  var generatedShades = shades.map(function (shade, index) {
    // -1 to make a zero indexed value
    var indexByLuminance = shades.length - 1 - Math.round(luminance * shades.length);
    if (indexByLuminance === index) {
      var hsl = convertToHsl(color);
      return hsl;
    } else if (indexByLuminance > index) {
      var remainingLuminance = maxLightenValue / indexByLuminance;
      var lightenValue = maxLightenValue - remainingLuminance * index;
      var newColor = lighten(lightenValue, color);
      var _hsl = convertToHsl(newColor);
      return _hsl;
    } else {
      var _remainingLuminance = maxDarkenValue / (shades.length - indexByLuminance);
      var darkenValue = maxDarkenValue - _remainingLuminance * (shades.length - index);
      var _newColor = darken(darkenValue, color);
      var _hsl2 = convertToHsl(_newColor);
      return _hsl2;
    }
  });
  var tokens = {
    50: generatedShades[0],
    100: generatedShades[1],
    200: generatedShades[2],
    300: generatedShades[3],
    400: generatedShades[4],
    500: generatedShades[5],
    600: generatedShades[6],
    700: generatedShades[7],
    800: generatedShades[8],
    900: generatedShades[9],
    950: generatedShades[10]
  };
  return tokens;
}
function generateColorShadesCss(property, color) {
  if (!property || !color) return;
  var css = '';
  var shades = generateColorShades(color);
  for (var shade in shades) {
    storeColorVariable("".concat(property, "-").concat(shade), shades[shade]);
    css += "".concat(property, "-").concat(shade, ":").concat(space).concat(shades[shade], ";").concat(lineBreak);
  }
  return css;
}
function generateLightAndDarkVariations(property, color) {
  if (!property || !color) return;
  var light = lighten(0.128, color);
  var dark = darken(0.128, color);
  var hslLight = convertToHsl(light);
  var hslDark = convertToHsl(dark);
  var css = '';
  css += "".concat(property, "-light:").concat(space).concat(hslLight, ";").concat(lineBreak);
  css += "".concat(property, "-dark:").concat(space).concat(hslDark, ";").concat(lineBreak);
  return css;
}

/**
 * Store a color variable in the dynamicStylesColors object. This is used in override background to know
 * know the contrast value in isBgColorStyleLight.
 * @param {string} colorVariable Color variable (e.g. --g-color-primary)
 * @param {string} value Color value in hsl (e.g. 0 0% 50%).
 */
function storeColorVariable(colorVariable, value) {
  dynamicStylesColors[colorVariable] = value;
}
export function generateColorVariables(property, color, tab) {
  if (!property || !color) return;
  var css = '';
  var hsl = storeAndConvertToHsl(property, color);
  css += "".concat(tab).concat(property, ":").concat(space).concat(hsl, ";").concat(lineBreak);
  if (!property.endsWith('0') && !property.endsWith('-light') && !property.endsWith('-dark') && !property.endsWith('-contrast') && !property.endsWith('-container')) {
    var readableColorValue = readableColor(color);
    var contrastColor = readableColorValue === '#000' ? 'var(--g-color-dark)' : 'var(--g-color-light)';
    var contrastColorState = readableColorValue === '#000' ? '0 0% 13%' : '0 0% 87%';
    css += generateLightAndDarkVariations(property, color);
    css += "".concat(tab).concat(property, "-contrast:").concat(space).concat(contrastColor, ";").concat(lineBreak);
    css += "".concat(tab).concat(property, "-contrast-state:").concat(space).concat(contrastColorState, ";").concat(lineBreak);
    storeColorVariable("".concat(property, "-contrast"), contrastColor);
  } else if (property.endsWith('-container')) {
    var _readableColorValue = readableColor(color);
    var _contrastColor = _readableColorValue === '#000' ? 'var(--g-color-dark)' : 'var(--g-color-light)';
    css += "".concat(tab).concat(property, "-contrast:").concat(space).concat(_contrastColor, ";").concat(lineBreak);
  }
  if (property === '--g-color-primary' || property === '--g-color-secondary' || property === '--g-color-tertiary' || property === '--g-color-neutral' || property === '--g-color-error' || property === '--g-color-success' || property === '--g-color-warning' || property === '--g-color-info') {
    var shades = generateColorShadesCss(property, color);
    css += shades;
  }
  return css;
}

/**
 * @param {Object} siteCollectionData siteCollectionData.siteCollection.items[0];
 * @param {Object} siteCollectionData.siteCollection
 * @param {Array} siteCollectionData.siteCollection.items
 * @param {Object} siteCollectionData.siteCollection.items[0]
 * @param {Object} headerData headerData.dynamicStyles.stylesCollection.items;
 * @param {Object} headerData.dynamicStyles
 * @param {Object} headerData.dynamicStyles.stylesCollection
 * @param {Array} headerData.dynamicStyles.stylesCollection.items
 * @param {Object} footerData footerData.dynamicStyles.stylesCollection.items;
 * @param {Object} footerData.dynamicStyles
 * @param {Object} footerData.dynamicStyles.stylesCollection
 * @param {Array} footerData.dynamicStyles.stylesCollection.items
 * @returns {string} CSS string
 */
function generateDynamicStylesCss(siteCollectionData, headerData, footerData) {
  var _siteCollectionData$s, _siteCollectionData$s2, _siteData$dynamicStyl, _siteData$dynamicStyl2, _headerData$dynamicSt, _headerData$dynamicSt2, _footerData$dynamicSt, _footerData$dynamicSt2;
  // parse dynamic styles
  var siteData = siteCollectionData === null || siteCollectionData === void 0 ? void 0 : (_siteCollectionData$s = siteCollectionData.siteCollection) === null || _siteCollectionData$s === void 0 ? void 0 : (_siteCollectionData$s2 = _siteCollectionData$s.items) === null || _siteCollectionData$s2 === void 0 ? void 0 : _siteCollectionData$s2[0];
  var stylesArray = siteData === null || siteData === void 0 ? void 0 : (_siteData$dynamicStyl = siteData.dynamicStyles) === null || _siteData$dynamicStyl === void 0 ? void 0 : (_siteData$dynamicStyl2 = _siteData$dynamicStyl.stylesCollection) === null || _siteData$dynamicStyl2 === void 0 ? void 0 : _siteData$dynamicStyl2.items;
  var dynamicStylesCss = jsonToCss(stylesArray) || '';

  // Add Header V2 Dynamic Styles
  if (headerData !== null && headerData !== void 0 && (_headerData$dynamicSt = headerData.dynamicStyles) !== null && _headerData$dynamicSt !== void 0 && (_headerData$dynamicSt2 = _headerData$dynamicSt.stylesCollection) !== null && _headerData$dynamicSt2 !== void 0 && _headerData$dynamicSt2.items) {
    var headerStylesArray = headerData.dynamicStyles.stylesCollection.items;
    dynamicStylesCss += jsonToCss(headerStylesArray) || '';
  }

  // Add Footer V2 Dynamic Styles
  if (footerData !== null && footerData !== void 0 && (_footerData$dynamicSt = footerData.dynamicStyles) !== null && _footerData$dynamicSt !== void 0 && (_footerData$dynamicSt2 = _footerData$dynamicSt.stylesCollection) !== null && _footerData$dynamicSt2 !== void 0 && _footerData$dynamicSt2.items) {
    var footerStylesArray = footerData.dynamicStyles.stylesCollection.items;
    dynamicStylesCss += jsonToCss(footerStylesArray) || '';
  }
  return dynamicStylesCss;
}
export default {
  generateDynamicStylesCss: generateDynamicStylesCss,
  dynamicStylesFonts: dynamicStylesFonts,
  dynamicStylesColors: dynamicStylesColors
};